import { Routes } from '@angular/router';
import { authCanMatch, contextReadyCanActivate, permissionGuard, CallbackComponent, LogoutComponent } from '@lr/core';
import { AccessDeniedComponent, NotFoundComponent } from '@lr/ui';
import { hasFirmCanActivate } from './guards/has-firm.guard';
import { redirectFirmTab } from './guards/firm-tab.guard';

const sectionRoutes: Routes = [
  {
    path: 'members',
    loadComponent: () => import('./pages/members/members.component').then(m => m.MembersComponent),
    canMatch: [authCanMatch],
    canActivate: [contextReadyCanActivate, hasFirmCanActivate, permissionGuard('Member.View')],
    data: { requiredPermission: 'Member.View' },
    title: 'Members'
  },
  {
    path: 'visits',
    loadComponent: () => import('./pages/visits/visits.component').then(m => m.VisitsComponent),
    canMatch: [authCanMatch],
    canActivate: [contextReadyCanActivate, hasFirmCanActivate, permissionGuard('MemberVisit.Manage')],
    data: { requiredPermission: 'MemberVisit.Manage' },
    title: 'Visit history'
  },
  {
    path: 'statements',
    loadComponent: () => import('./pages/statements/statements.component').then(m => m.StatementsComponent),
    canMatch: [authCanMatch],
    canActivate: [contextReadyCanActivate, hasFirmCanActivate, permissionGuard('Statement.View')],
    data: { requiredPermission: 'Statement.View' },
    title: 'Statements'
  },
  {
    path: 'firm',
    loadComponent: () => import('./pages/firm/firm.component').then(m => m.FirmComponent),
    canMatch: [authCanMatch],
    canActivate: [contextReadyCanActivate, hasFirmCanActivate],
    title: 'Firm profile',
    children: [
      {
        path: '',
        pathMatch: 'full',
        canActivate: [redirectFirmTab],
        loadComponent: () => import('./pages/firm/firm-panel.component').then(m => m.FirmPanelComponent)
      },
      {
        path: 'details',
        canActivate: [permissionGuard('Firm.View')],
        data: { tab: 'details', requiredPermission: 'Firm.View' },
        title: 'Firm details',
        loadComponent: () => import('./pages/firm/firm-panel.component').then(m => m.FirmPanelComponent)
      },
      {
        path: 'offices',
        canActivate: [permissionGuard('Firm.View')],
        data: { tab: 'offices', requiredPermission: 'Firm.View' },
        title: 'Offices',
        loadComponent: () => import('./pages/firm/firm-panel.component').then(m => m.FirmPanelComponent)
      },
      {
        path: 'contacts',
        canActivate: [permissionGuard('Firm.View')],
        data: { tab: 'contacts', requiredPermission: 'Firm.View' },
        title: 'Contacts',
        loadComponent: () => import('./pages/firm/firm-panel.component').then(m => m.FirmPanelComponent)
      },
      {
        path: 'documents',
        canActivate: [permissionGuard('Document.View')],
        data: { tab: 'documents', requiredPermission: 'Document.View' },
        title: 'Documents',
        loadComponent: () => import('./pages/firm/firm-panel.component').then(m => m.FirmPanelComponent)
      }
    ]
  },
  {
    path: 'users',
    loadComponent: () => import('./pages/users/users.component').then(m => m.UsersComponent),
    canMatch: [authCanMatch],
    canActivate: [contextReadyCanActivate, hasFirmCanActivate, permissionGuard('User.View')],
    data: { requiredPermission: 'User.View' },
    title: 'Users and access'
  },
  {
    path: 'users/new',
    loadComponent: () => import('./pages/users/user-form.component').then(m => m.UserFormComponent),
    canMatch: [authCanMatch],
    canActivate: [contextReadyCanActivate, hasFirmCanActivate, permissionGuard('User.Manage')],
    data: { requiredPermission: 'User.Manage' },
    title: 'Add New User'
  },
  {
    path: 'users/:portalUserId',
    loadComponent: () => import('./pages/users/user-form.component').then(m => m.UserFormComponent),
    canMatch: [authCanMatch],
    canActivate: [contextReadyCanActivate, hasFirmCanActivate, permissionGuard('User.Manage')],
    data: { requiredPermission: 'User.Manage' },
    title: 'Edit User'
  }
];

export const routes: Routes = [
  {
    path: 'callback',
    component: CallbackComponent,
    title: 'Signing in…'
  },
  {
    path: 'login',
    loadComponent: () => import('./pages/login/login.component').then(m => m.LoginComponent),
    canMatch: [authCanMatch],
    title: 'Login'
  },
  {
    path: 'logout',
    component: LogoutComponent,
    title: 'Logout'
  },
  {
    path: 'firm-selection',
    loadComponent: () =>
      import('./pages/firm-selection/firm-selection.component').then(m => m.FirmSelectionComponent),
    canMatch: [authCanMatch],
    canActivate: [contextReadyCanActivate],
    title: 'Select Firm'
  },
  {
    path: '',
    redirectTo: 'home',
    pathMatch: 'full'
  },
  {
    path: 'home',
    loadComponent: () => import('./pages/home/home.component').then(m => m.HomeComponent),
    canMatch: [authCanMatch],
    canActivate: [contextReadyCanActivate, hasFirmCanActivate],
    title: 'Home'
  },
  ...sectionRoutes,
  {
    path: 'access-denied',
    component: AccessDeniedComponent,
    title: 'Access denied'
  },
  {
    path: 'unauthorized',
    component: AccessDeniedComponent,
    title: 'Unauthorized'
  },
  {
    path: 'home',
    loadComponent: () => import('@lr/core').then(m => m.InitializingComponent)
  },
  {
    path: '**',
    component: NotFoundComponent
  }
];
